import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * セグメント区間（start〜end）のクライアント側頭出し再生（Unit 006 / 008）。
 * 再生可否は保持音声の状態（audio_available＝保持キー＋未期限・所有権）に従い、現在プランに依存しない
 * （Unit 008 / Option B: unlimited で生成→降格しても保持期限内は再生可）。
 *
 * 保持音声全体を1本の署名付きURLで配信し、単一の <audio> 要素を共有して
 * currentTime=start へ頭出し → end 到達で停止する。サーバ側の区間切り出しは行わない。
 *
 * 区間末尾の停止精度: HTML5 `timeupdate` は約250ms粒度のため、`currentTime >= end` で pause する。
 * 末尾セグメント（end が音声全長に近接）では timeupdate が end に届かないことがあるため
 * `ended` イベントも併用して確実に停止状態へ戻す。
 */
export interface SegmentPlaybackControls {
  /** 現在再生中のセグメントキー（未再生は null） */
  playingKey: string | null;
  /** 指定区間を頭出し再生する */
  play: (start: number, end: number, key: string) => void;
  /** 再生を停止する */
  stop: () => void;
}

export function useSegmentPlayback(audioUrl?: string | null): SegmentPlaybackControls {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stopAtRef = useRef<number | null>(null);
  // 保留中の loadedmetadata リスナー（連続 play / unmount で確実に外すため保持）
  const pendingLoadedRef = useRef<(() => void) | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  // audioUrl が変わったら <audio> を作り直す（期限切れ→null でクリーンアップ）
  useEffect(() => {
    if (!audioUrl) {
      audioRef.current = null;
      stopAtRef.current = null;
      setPlayingKey(null);
      return;
    }

    const audio = new Audio(audioUrl);
    audio.preload = 'metadata';
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      const stopAt = stopAtRef.current;
      if (stopAt != null && audio.currentTime >= stopAt) {
        audio.pause();
        stopAtRef.current = null;
        setPlayingKey(null);
      }
    };
    const handleEnded = () => {
      stopAtRef.current = null;
      setPlayingKey(null);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      if (pendingLoadedRef.current) {
        audio.removeEventListener('loadedmetadata', pendingLoadedRef.current);
        pendingLoadedRef.current = null;
      }
      audioRef.current = null;
      stopAtRef.current = null;
    };
  }, [audioUrl]);

  const play = useCallback((start: number, end: number, key: string) => {
    const audio = audioRef.current;
    if (!audio) return;

    stopAtRef.current = end;

    const startPlayback = () => {
      // 前区間の再生中に別区間が要求された場合の取り違え（古い timeupdate による即停止）を避けるため、
      // seek の前に一旦停止してから頭出し→再生する。
      audio.pause();
      try {
        audio.currentTime = start;
      } catch {
        // seek 失敗は再生開始のみ試みる
      }
      audio.play().then(() => setPlayingKey(key)).catch(() => setPlayingKey(null));
    };

    // 連続再生で古い loadedmetadata リスナーが残らないよう、保留中があれば先に外す
    if (pendingLoadedRef.current) {
      audio.removeEventListener('loadedmetadata', pendingLoadedRef.current);
      pendingLoadedRef.current = null;
    }

    // メタデータ未ロードだと seek が効かないため、ロード後に頭出しする
    if (audio.readyState >= 1) {
      startPlayback();
    } else {
      const onLoaded = () => {
        audio.removeEventListener('loadedmetadata', onLoaded);
        pendingLoadedRef.current = null;
        startPlayback();
      };
      pendingLoadedRef.current = onLoaded;
      audio.addEventListener('loadedmetadata', onLoaded);
      audio.load();
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    stopAtRef.current = null;
    setPlayingKey(null);
  }, []);

  return { playingKey, play, stop };
}
